import { Data } from '@dnd-kit/core';
import {
  rectSwappingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CSSProperties, Fragment, Key, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { HandlerIcon } from '../Icons';
import { Alignment } from '../Table/types';

export type ColumnDef<T> = {
  id: Key;
  name: string;
  accessor?: keyof T;
  render?: () => ReactNode;
  renderRow?: (row: T) => ReactNode;
  alignment?: Alignment;
  rowContainerClassNames?: string;
  tdClassNames?: string;
};

export type RowDef<T> = { id: Key } & T;

type RowProps<T> = {
  row: RowDef<T>;
  columns: ColumnDef<T>[];
  handlerIndex: number;
  nonDraggableColumnKeys?: string[];
};

const Row = <T extends Data>({
  row,
  columns,
  handlerIndex,
  nonDraggableColumnKeys,
}: RowProps<T>) => {
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({
      id: row.id.toString(),
      data: row,
    });

  const draggableStyles: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef}>
      {columns.map((column, index) => (
        <Fragment key={`${column.id}_${row.id}`}>
          <td
            style={
              !nonDraggableColumnKeys?.includes(column.id.toString())
                ? draggableStyles
                : undefined
            }
            align={column.alignment ?? 'center'}
            className={twMerge(
              'group m-0 border-collapse border-spacing-0 border-b-4 border-transparent p-0',
              column.tdClassNames,
            )}
          >
            <div
              className={twMerge(
                'flex h-11.25 items-center justify-center overflow-hidden overflow-ellipsis bg-white px-2.5 text-sm text-primaryBlue',
                'border-b-1 border-t-1 border-rinseGray group-first:rounded-l-md group-first:border-l group-last:rounded-r-md group-last:border-r group-last:border-t',
                column.alignment === 'left' && 'justify-start',
                column.alignment === 'right' && 'justify-end',
                column.rowContainerClassNames,
              )}
            >
              {column.renderRow?.(row) ??
                (column.accessor && (
                  <span className="w-full overflow-hidden text-ellipsis text-nowrap">
                    {row[column.accessor]}
                  </span>
                ))}
            </div>
          </td>
          {index === handlerIndex && (
            <td
              style={draggableStyles}
              align={'center'}
              className={twMerge(
                'group m-0 border-collapse border-spacing-0 border-b-4 border-transparent p-0',
              )}
            >
              <div
                className={twMerge(
                  `flex h-11.25 w-full cursor-pointer items-center justify-center bg-white px-1 text-sm text-primaryBlue`,
                  `border-b-1 border-t-1 border-rinseGray group-first:rounded-l-md group-first:border-l group-last:rounded-r-md group-last:border-r group-last:border-t`,
                )}
                {...attributes}
                {...listeners}
              >
                <HandlerIcon />
              </div>
            </td>
          )}
        </Fragment>
      ))}
    </tr>
  );
};

export type DraggableTableProps<T> = {
  rows: RowDef<T>[];
  columns: ColumnDef<T>[];
  handlerIndex: number;
  nonDraggableColumnKeys: string[];
  emptyStateMessage: ReactNode;
  isDragSubmitting?: boolean;
};

export const DraggableTable = <T extends Data>({
  rows,
  columns,
  handlerIndex,
  nonDraggableColumnKeys,
  emptyStateMessage,
  isDragSubmitting,
}: DraggableTableProps<T>) => {
  return (
    <table className="w-full">
      <thead>
        <tr>
          {columns.map((column, index) => (
            <Fragment key={column.id}>
              <th
                align={column.alignment ?? 'center'}
                className={twMerge(
                  'h-8 border-b-2.5 border-transparent bg-inherit px-2.5 text-xs font-black leading-xs text-spartanBlue',
                  column.tdClassNames,
                )}
              >
                {column.render?.() ?? column.name}
              </th>
              {index === handlerIndex && (
                <th
                  align="center"
                  className={twMerge(
                    'h-8 border-b-2.5 border-transparent bg-inherit text-xs font-black leading-xs text-spartanBlue',
                  )}
                ></th>
              )}
            </Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        <SortableContext
          items={rows.map((row) => row.id.toString())}
          strategy={rectSwappingStrategy}
          disabled={isDragSubmitting}
        >
          {!rows.length && !!emptyStateMessage && (
            <tr className="h-16">
              <td colSpan={columns.length} align="center" className="text-md">
                {emptyStateMessage}
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <Row
              key={row.id}
              row={row}
              columns={columns}
              handlerIndex={handlerIndex}
              nonDraggableColumnKeys={nonDraggableColumnKeys}
            />
          ))}
        </SortableContext>
      </tbody>
    </table>
  );
};
